import React, { useEffect, useRef } from 'react'
import { selectSimulationResult } from '../../redux/reducers/simulationResult.reducer'
import { useAppSelector } from '../../redux/store'
import { Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, registerables } from 'chart.js'
import { Widget as TypeFormWidget } from '@typeform/embed-react'

import styled from '@emotion/styled'
import { mq } from '../breakpoints'
import WidgetMyBooster from '../WidgetMyBooster'
ChartJS.register(...registerables)

const currencyFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
})

export const SimulationResult = () => {
  const resultRef = useRef(null)
  const simulationResult = useAppSelector(selectSimulationResult)

  useEffect(() => {
    // Le timeout permet d'éviter que le scroll automatique n'ait pas lieu
    // car la dimension de l'iframe va changer au même moment sur la page parente et le scroll ne
    // se terminera pas correctement si on tente de le faire en même temps
    const timeout = setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 300)

    return () => {
      clearTimeout(timeout)
    }
  }, [simulationResult])

  if (!simulationResult) {
    return <></>
  }

  return (
    <>
      <Header ref={resultRef}>
        Votre résultat<sup>2</sup>&nbsp;:
      </Header>
      <Container>
        <DoughnutContainer>
          <Doughnut
            data={{
              datasets: [
                {
                  data: [
                    simulationResult.ssAmount,
                    simulationResult.insuranceAmount,
                    simulationResult.meAmount,
                  ],
                  backgroundColor: ['#009ADC', '#31D0AA', '#FF41AE'],
                },
              ],
            }}
          />
          <ChartLabel>
            {currencyFormatter.format(simulationResult.careAmount)}
          </ChartLabel>
        </DoughnutContainer>
        <AmountContainer>
          <PillContainer>
            <Pill color='#009ADC'>
              Sécurité sociale{' '}
              {currencyFormatter.format(simulationResult.ssAmount)}
            </Pill>
            <Pill color='#31D0AA'>
              Mutuelle{' '}
              {currencyFormatter.format(simulationResult.insuranceAmount)}
            </Pill>
          </PillContainer>

          <MeAmount>
            Votre reste à charge&nbsp;:
            <br />
            <TextBold>
              {currencyFormatter.format(simulationResult.meAmount)}
            </TextBold>{' '}
            sur ce soin
          </MeAmount>
        </AmountContainer>
      </Container>
      { simulationResult.meAmount > 0 && <WidgetMyBooster/> }
      <FeedBackContainer>
        <TypeFormWidget
          id='GasAEGaP'
          autoResize={true}
          inlineOnMobile={true}
          style={{
            width: '100%',
            marginBottom: '30px',
          }}
        />
      </FeedBackContainer>
    </>
  )
}

const Container = styled.div`
  padding: 20px;

  ${mq.lg} {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const FeedBackContainer = styled(Container)`
  border-top: 2px solid #f2f2f2;
`

const DoughnutContainer = styled.div`
  margin: 0 auto;
  width: 240px;
  height: 240px;
  position: relative;

  ${mq.lg} {
    margin: inherit;
  }
`

const ChartLabel = styled.div`
  position: absolute;
  height: 120px;
  width: 120px;
  top: calc(50% - 60px);
  left: calc(50% - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.5rem;
  background-color: white;
  border-radius: 9999px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
`

const AmountContainer = styled.div`
  ${mq.lg} {
    margin-left: 30px;
  }
`

const PillContainer = styled.div`
  margin-top: 30px;
  margin-bottom: 60px;
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  align-items: center;

  ${mq.lg} {
    margin-top: 0;
    margin-bottom: 30px;
  }
`

const Pill = styled.div<{ color: string }>`
  border-radius: 9999px;
  background-color: ${props => props.color};
  width: 100%;
  max-width: 450px;
  min-width: 260px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.125rem;

  ${mq.lg} {
    width: 340px;
  }
`

const TextBold = styled.span`
  font-weight: bold;
  font-size: 1.5rem;
`

const MeAmount = styled.div`
  font-weight: 400;
  font-size: 1.125rem;
  text-align: center;
  color: #fff;
  width: 100%;
  min-width: 260px;
  max-width: 450px;
  background-color: #ff41ae;
  border-radius: 10px;
  padding: 20px;
  margin: 0 auto;
  box-sizing: border-box;
`

const Header = styled.div`
  font-size: 2.25rem;
  font-weight: 600;
  background-color: #f2f2f2;
  padding: 30px;
  text-align: center;

  sup {
    font-size: 0.85rem;
    margin-bottom: 15px;
    font-weight: normal;
    vertical-align: top;
  }
`
